import React, { useEffect, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const MOVE_LABELS = { in: "Entrada", out: "Saída", adjust: "Ajuste (definido)", delta: "Ajuste (+/−)" };

const MONTHLY_COLS = [
  { k: "month", label: "Mês" }, { k: "quantity", label: "Qtd", right: true },
  { k: "revenue", label: "Faturado", right: true, render: (r) => brl(r.revenue) },
];
const PURCHASE_COLS = [
  { k: "date", label: "Data", render: (r) => dateBR(r.date) }, { k: "supplier", label: "Fornecedor", render: (r) => r.supplier || "—" },
  { k: "quantity", label: "Qtd", right: true }, { k: "cost_price", label: "Custo un.", right: true, render: (r) => brl(r.cost_price) },
  { k: "status", label: "Situação", render: (r) => <Badge variant="outline">{r.status === "pending" ? "A receber" : "Recebida"}</Badge> },
];
const MOVE_COLS = [
  { k: "date", label: "Data", render: (r) => dateBR(r.date) }, { k: "type", label: "Tipo", render: (r) => MOVE_LABELS[r.type] || r.type },
  { k: "quantity", label: "Qtd", right: true },
  { k: "new_stock", label: "Saldo", right: true, render: (r) => (r.new_stock == null ? "—" : `${r.previous_stock} → ${r.new_stock}`) },
  { k: "reason", label: "Motivo", render: (r) => r.reason || "—" },
];

const Kpi = ({ label, value, tone = "" }) => (
  <div className="border rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className={"kpi-num text-lg mt-1 " + tone}>{value}</div>
  </div>
);

const SimpleTable = ({ cols, rows, empty, tid }) => (
  <div className="max-h-72 overflow-y-auto border rounded-lg" data-testid={tid}>
    <Table>
      <TableHeader><TableRow>{cols.map((c) => <TableHead key={c.k} className={c.right ? "text-right" : ""}>{c.label}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={r.id ? `${r.id}-${i}` : i}>
            {cols.map((c) => <TableCell key={c.k} className={"text-sm " + (c.right ? "text-right kpi-num" : "")}>{c.render ? c.render(r) : r[c.k]}</TableCell>)}
          </TableRow>
        ))}
        {rows.length === 0 && <TableRow><TableCell colSpan={cols.length} className="text-center text-slate-400 py-6">{empty}</TableCell></TableRow>}
      </TableBody>
    </Table>
  </div>
);

export const ProductOverviewDialog = ({ row, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (row) { api.get(`/products/${row.id}/overview`).then((r) => setData(r.data)).catch(() => setData(null)); }
  }, [row]);

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-overview-dialog">
        <DialogHeader><DialogTitle className="font-display">{row?.name}</DialogTitle></DialogHeader>
        {row && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi label="Estoque" value={row.type === "product" ? row.stock : "—"} />
              <Kpi label="Comprado" value={`${row.purchased_qty} • ${brl(row.purchased_amount)}`} />
              <Kpi label="Vendido" value={`${row.sold_qty} • ${brl(row.revenue)}`} tone="text-emerald-600" />
              <Kpi label="Lucro bruto" value={brl(row.profit)} tone={row.profit < 0 ? "text-rose-600" : "text-blue-600"} />
            </div>
            <Tabs defaultValue="sales">
              <TabsList>
                <TabsTrigger value="sales" data-testid="ov-tab-sales">Vendas por mês</TabsTrigger>
                <TabsTrigger value="purchases" data-testid="ov-tab-purchases">Compras</TabsTrigger>
                <TabsTrigger value="moves" data-testid="ov-tab-moves">Movimentos</TabsTrigger>
              </TabsList>
              <TabsContent value="sales">
                <SimpleTable tid="ov-monthly" empty="Sem vendas." rows={data?.monthly || []} cols={MONTHLY_COLS} />
              </TabsContent>
              <TabsContent value="purchases">
                <SimpleTable tid="ov-purchases" empty="Sem compras." rows={data?.purchases || []} cols={PURCHASE_COLS} />
              </TabsContent>
              <TabsContent value="moves">
                <SimpleTable tid="ov-moves" empty="Sem movimentos." rows={data?.movements || []} cols={MOVE_COLS} />
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
